import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { fetchPublicProfile } from '../../api/user';
import Card from '../../components/Card';
import CardPreviewModal from '../../components/CardPreviewModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import TrophyCase from '../../components/TrophyCase';
import BadgeCatalog from '../../components/BadgeCatalog';
import { toast } from 'react-toastify';


const getRankLabel = elo => {
    if (elo >= 1400) return 'Diamond';
    if (elo >= 1200) return 'Platinum';
    if (elo >= 1000) return 'Gold';
    if (elo >= 800) return 'Silver';
    return 'Bronze';
};

const PublicProfilePage = () => {
    const { username } = useParams();
    const [currentUser] = useAtom(currentUserAtom);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewCard, setPreviewCard] = useState(null);

    const isOwnProfile = currentUser?.username === username;

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchPublicProfile(username);
                if (cancelled) return;
                setProfile(data);
            } catch (err) {
                console.error('[PublicProfile] load failed:', err);
                if (!cancelled) setError('Player not found');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();

        return () => { cancelled = true; };
    }, [username]);

    const handleShare = () => {
        navigator.clipboard.writeText(`${window.location.origin}/u/${username}`);
        toast.success('Profile link copied!');
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <div className="public-profile"><p className="error">{error}</p></div>;
    if (!profile) return null;

    const { trophies, binders, featuredTeam, collectionStats } = profile;
    const lineups = featuredTeam?.UserTeamLineups ?? [];
    const starters = lineups
        .filter(l => l.lineupOrder && l.lineupOrder <= 9)
        .sort((a, b) => a.lineupOrder - b.lineupOrder);

    return (
        <div className="public-profile">
            {/* Top CTA — soft banner for non-players */}
            {!currentUser && (
                <div className="profile-cta-top">
                    <span>Uranium Baseball — collect, compete, dominate.</span>
                    <Link to="/register" className="button profile-cta-top-link">Sign Up Free</Link>
                </div>
            )}

            {/* Hero */}
            <div className="profile-hero">
                <div className="profile-hero-label">Player Profile</div>
                <div className="profile-hero-name">
                    {profile.username}
                    {profile.leagueName && (
                        <span className={`league-badge league-badge-${profile.league}`}>{profile.leagueName}</span>
                    )}
                </div>
                <div className="profile-hero-stats">
                    {profile.elo != null && (
                        <div className="profile-hero-stat">
                            <span className="profile-hero-stat-value profile-rank">{getRankLabel(profile.elo)}</span>
                            <span className="profile-hero-stat-label">Rank</span>
                        </div>
                    )}
                    {profile.levelInfo?.level != null && (
                        <div className="profile-hero-stat">
                            <span className="profile-hero-stat-value">Lv {profile.levelInfo.level}</span>
                            <span className="profile-hero-stat-label">Level</span>
                        </div>
                    )}
                </div>
                {isOwnProfile && (
                    <button className="profile-share-btn" onClick={handleShare} title="Copy profile link">
                        Share
                    </button>
                )}
            </div>

            {/* 1. Starting Nine — the visual showstopper */}
            {starters.length > 0 && (
                <div className="profile-section">
                    <div className="profile-section-title">Starting Nine</div>
                    <div className="profile-team">
                        {starters.map(lineup => {
                            const card = lineup.CardInventory?.Card;
                            const variation = lineup.CardInventory?.CardVariation;
                            if (!card) return null;
                            return (
                                <div
                                    key={lineup.lineupID}
                                    className="profile-team-card"
                                >
                                    <Card
                                        card={{ ...card, CardInventory: lineup.CardInventory }}
                                        variation={variation}
                                        showLabel
                                        hoverFlip
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 2. Badges */}
            {profile.badges?.earned?.length > 0 ? (
                <div className="profile-section">
                    <div className="profile-section-title">Badges</div>
                    <BadgeCatalog badges={profile.badges} />
                </div>
            ) : null}

            {/* 3. Trophies — credibility */}
            <div className="profile-section">
                <div className="profile-section-title">Trophies</div>
                <TrophyCase trophies={trophies} />
            </div>

            {/* 3. Collection Stats — quick flex */}
            <div className="profile-stats-row">
                <div className="profile-stat-box">
                    <span className="profile-stat-value">{collectionStats.total}</span>
                    <span className="profile-stat-label">Cards</span>
                </div>
                <div className="profile-stat-box">
                    <span className="profile-stat-value uranium-text">{collectionStats.uranium}</span>
                    <span className="profile-stat-label">Uranium</span>
                </div>
                <div className="profile-stat-box">
                    <span className="profile-stat-value gold-text">{collectionStats.gold}</span>
                    <span className="profile-stat-label">Gold</span>
                </div>
            </div>

            {/* 4. Binders — deep dive */}
            {binders.length > 0 && (
                <div className="profile-section">
                    <div className="profile-section-title">Binders</div>
                    <div className="profile-binders">
                        {binders.map(b => (
                            <Link
                                key={b.binderID}
                                to={`/${username}/binder/${b.binderNumber}`}
                                className="profile-binder-tile"
                            >
                                {b.title}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* 5. Bottom CTA — for convinced scrollers */}
            {!currentUser && (
                <div className="profile-cta">
                    <p className="profile-cta-text">Start collecting. Start competing.</p>
                    <Link to="/register" className="profile-cta-button">Play Now</Link>
                </div>
            )}

            {/* Card preview modal */}
            {previewCard && (
                <CardPreviewModal card={previewCard} onClose={() => setPreviewCard(null)} />
            )}
        </div>
    );
};

export default PublicProfilePage;
